import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaunchIcon from '@mui/icons-material/Launch';
import { useTheme } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';

import {
  productsApi,
  pricePerRoundNumber,
  type NormalizedProduct,
  type ProductDetailResponse,
  type ProductListing,
} from '../services/productsApi';
import { ApiError } from '../services/api';
import { searchListings, type SearchHit, type SearchResponse } from '../services/searchApi';
import { ListingCard } from '../components/search/ListingCard';
import { Seo } from '../components/seo/Seo';
import { buildCanonicalUrl } from '../utils/seo/canonical';
import { breadcrumbList, product as productJsonLd } from '../utils/seo/jsonLd';
import { WatchButton } from '../components/watchlist/WatchButton';
import { userApi } from '../services/userApi';
import { useAuthStore } from '../stores/authStore';
import { PriceHistoryChart } from '../components/common/PriceHistoryChart';
import { Cartridge } from '../components/brand/Cartridge';
import { Eyebrow } from '../components/brand/Eyebrow';
import { StockPill } from '../components/brand/StockPill';
import { PricePosition } from '../components/brand/PricePosition';
import { effectiveCpr, effectiveRoundCount, offerPriceValidUntil } from '../utils/pricing';
import { trackViewItem, trackOutboundClick as gaTrackOutboundClick } from '../lib/analytics/events';

type SortField = 'pricePerRound' | 'price' | 'roundCount' | 'retailer' | 'freshness';
type SortDir = 'asc' | 'desc';

const FRESHNESS_LABEL: Record<ProductListing['dataFreshness'], string> = {
  fresh: 'Fresh',
  aging: 'Aging',
  stale: 'Stale',
  unavailable: 'Unavailable',
};

const FRESHNESS_COLOR: Record<
  ProductListing['dataFreshness'],
  'success' | 'warning' | 'error' | 'default'
> = {
  fresh: 'success',
  aging: 'warning',
  stale: 'error',
  unavailable: 'default',
};

const STOCK_LABEL: Record<ProductListing['stockStatus'], string> = {
  in_stock: 'In stock',
  out_of_stock: 'Out of stock',
  limited: 'Limited',
  unknown: 'Unknown',
};

/**
 * Beginner-friendly explanations of ammunition terms. Shared with the
 * search-results sidebar (Phase 3) — keep terms lowercased keys.
 * Per spec §15.5 ("Attribute explanations").
 */
const ATTRIBUTE_EXPLANATIONS: Array<{ term: string; explanation: string }> = [
  {
    term: 'JHP',
    explanation: 'Jacketed Hollow Point — designed to expand on impact, typical for self-defense.',
  },
  {
    term: 'FMJ',
    explanation: 'Full Metal Jacket — non-expanding round, common for range/training use.',
  },
  { term: 'HP', explanation: 'Hollow Point — bullet tip designed to expand on impact.' },
  {
    term: 'Brass',
    explanation:
      'Brass cases are reloadable and corrosion-resistant; the most common case material.',
  },
  {
    term: 'Steel',
    explanation: 'Steel cases are cheaper but not reloadable and can wear extractors over time.',
  },
  {
    term: 'Reloaded',
    explanation:
      'Remanufactured ammunition using once-fired brass — usually cheaper, never use in carry guns.',
  },
  {
    term: 'Subsonic',
    explanation: 'Travels below the speed of sound — quieter, often used with suppressors.',
  },
];

function formatGrains(value: NormalizedProduct['bulletWeightGrains']): string {
  if (value === null || value === undefined) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? `${n} gr` : '—';
}

function formatMoney(value: ProductListing['price'], currency = 'USD'): string {
  if (value === null || value === undefined) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function formatCpr(value: ProductListing['pricePerRound']): string {
  if (value === null || value === undefined) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(3)}/rd`;
}

function formatRoundCount(value: number | null): string {
  return value == null ? '—' : value.toLocaleString();
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function buildHeading(product: NormalizedProduct): string {
  const parts = [
    product.brand,
    product.productLine,
    product.caliber,
    product.bulletWeightGrains == null ? null : formatGrains(product.bulletWeightGrains),
    product.bulletType,
  ].filter((p): p is string => Boolean(p && String(p).trim()));
  return parts.length > 0 ? parts.join(' ') : 'Product';
}

/**
 * Build a one-sentence product description suitable for `<meta
 * name="description">` and JSON-LD `description`. Keeps it under
 * ~160 chars and incorporates the retailer comparison hook.
 */
function buildProductDescription(data: ProductDetailResponse): string {
  const heading = buildHeading(data.product);
  const n = data.listings.length;
  if (n === 0) return `${heading} — compare prices across retailers on Round Recon.`;
  const inStock = data.listings.filter(
    (l) => l.stockStatus === 'in_stock' || l.stockStatus === 'limited'
  ).length;
  return `Compare ${heading} prices at ${n} retailers on Round Recon (${inStock} in stock). Sorted by price per round, freshness, and retailer trust.`;
}

function aggregateConfidence(
  product: NormalizedProduct,
  listings: ProductListing[]
): number | null {
  const candidates: number[] = [];
  if (product.confidenceScore != null) {
    const n =
      typeof product.confidenceScore === 'string'
        ? Number(product.confidenceScore)
        : product.confidenceScore;
    if (Number.isFinite(n)) candidates.push(n);
  }
  for (const l of listings) {
    if (l.confidenceScore == null) continue;
    const n = typeof l.confidenceScore === 'string' ? Number(l.confidenceScore) : l.confidenceScore;
    if (Number.isFinite(n)) candidates.push(n);
  }
  if (candidates.length === 0) return null;
  return candidates.reduce((a, b) => a + b, 0) / candidates.length;
}

function worstFreshness(listings: ProductListing[]): ProductListing['dataFreshness'] {
  const order: ProductListing['dataFreshness'][] = ['unavailable', 'stale', 'aging', 'fresh'];
  for (const f of order) {
    if (listings.some((l) => l.dataFreshness === f)) return f;
  }
  return 'unavailable';
}

/**
 * Outbound-click tracker (#256). Sends a GA4 `select_item` event (consent-gated
 * inside the helper). The first-party ClickEvent is still recorded server-side
 * by the /api/out redirect; this adds the GA4 signal for outbound CTR.
 */
function trackOutboundClick(args: {
  listingId: string;
  retailerId: string;
  sourceUrl: string;
  retailer?: string | null;
  productName?: string;
  caliber?: string | null;
}): void {
  gaTrackOutboundClick({
    listingId: args.listingId,
    productName: args.productName,
    retailer: args.retailer ?? undefined,
    caliber: args.caliber ?? undefined,
  });
}

interface SortState {
  field: SortField;
  dir: SortDir;
}

function compareListings(a: ProductListing, b: ProductListing, sort: SortState): number {
  const dir = sort.dir === 'asc' ? 1 : -1;
  switch (sort.field) {
    case 'pricePerRound':
      return (pricePerRoundNumber(a) - pricePerRoundNumber(b)) * dir;
    case 'price': {
      const av = a.price == null ? Number.POSITIVE_INFINITY : Number(a.price);
      const bv = b.price == null ? Number.POSITIVE_INFINITY : Number(b.price);
      return (av - bv) * dir;
    }
    case 'roundCount': {
      const av = a.roundCount ?? Number.POSITIVE_INFINITY;
      const bv = b.roundCount ?? Number.POSITIVE_INFINITY;
      return (av - bv) * dir;
    }
    case 'retailer': {
      const an = a.Retailer?.name ?? '';
      const bn = b.Retailer?.name ?? '';
      return an.localeCompare(bn) * dir;
    }
    case 'freshness': {
      const rank: Record<ProductListing['dataFreshness'], number> = {
        fresh: 0,
        aging: 1,
        stale: 2,
        unavailable: 3,
      };
      return (rank[a.dataFreshness] - rank[b.dataFreshness]) * dir;
    }
    default:
      return 0;
  }
}

function ProductInfoCard({ product }: { product: NormalizedProduct }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-labelledby="product-info-heading"
    >
      <Typography id="product-info-heading" variant="h2" sx={{ fontSize: '1.25rem', mb: 2 }}>
        Product details
      </Typography>
      <Box
        component="dl"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
          columnGap: 2,
          rowGap: 1,
          m: 0,
        }}
      >
        <DetailRow label="Brand" value={product.brand} />
        <DetailRow label="Product line" value={product.productLine} />
        <DetailRow label="Caliber" value={product.caliber} />
        <DetailRow label="Bullet weight" value={formatGrains(product.bulletWeightGrains)} />
        <DetailRow label="Bullet type" value={product.bulletType} />
        <DetailRow label="Case material" value={product.caseMaterial} />
        <DetailRow label="Primer type" value={product.primerType} />
        <DetailRow label="UPC" value={product.upc} />
        <DetailRow label="MPN" value={product.manufacturerPartNumber} />
      </Box>

      {product.useCases && product.useCases.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
            Use cases
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {product.useCases.map((uc) => (
              <Chip key={uc} label={uc} size="small" />
            ))}
          </Stack>
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
        {product.isReloaded && <Chip label="Reloaded" color="warning" size="small" />}
        {product.isSubsonic && <Chip label="Subsonic" color="info" size="small" />}
        {product.isLeadFree && <Chip label="Lead-free" color="success" size="small" />}
      </Stack>
    </Paper>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  const text = value == null ? '' : String(value).trim();
  // #119: omit the row entirely when there's no value rather than showing a dash.
  // Also covers pre-formatted values (e.g. formatGrains) that emit the dash.
  if (!text || text === '—') return null;
  return (
    <>
      <Typography component="dt" variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0 }}>
        {text}
      </Typography>
    </>
  );
}

function ConfidenceFreshnessBanner({
  confidence,
  freshness,
}: {
  confidence: number | null;
  freshness: ProductListing['dataFreshness'];
}) {
  const severity = freshness === 'stale' || freshness === 'unavailable' ? 'warning' : 'info';
  const confidencePct = confidence == null ? null : Math.round(confidence * 100);
  const tooltip =
    'Confidence reflects how certain we are that we parsed each field correctly. ' +
    'Freshness reflects how recently the retailer data was verified. Older or low-confidence ' +
    'data should be double-checked on the retailer site before purchase.';
  return (
    <Tooltip title={tooltip} arrow>
      <Alert severity={severity} role="status" sx={{ alignItems: 'center' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <span>
            <strong>Aggregate confidence:</strong>{' '}
            {confidencePct == null ? 'unknown' : `${confidencePct}%`}
          </span>
          <span>
            <strong>Worst listing freshness:</strong> {FRESHNESS_LABEL[freshness]}
          </span>
          <span style={{ fontStyle: 'italic' }}>Hover for what this means</span>
        </Stack>
      </Alert>
    </Tooltip>
  );
}

function RetailerComparisonTable({
  listings,
  sort,
  onSortChange,
  onOutboundClick,
  watchedByListingId,
  onWatchChanged,
}: {
  listings: ProductListing[];
  sort: SortState;
  onSortChange: (field: SortField) => void;
  onOutboundClick: (listing: ProductListing) => void;
  watchedByListingId: Map<string, string>;
  onWatchChanged: () => void;
}) {
  if (listings.length === 0) {
    return (
      <Alert severity="info" role="status">
        No retailers are currently selling this product. Check back soon — we re-scrape regularly.
      </Alert>
    );
  }
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table aria-label="Retailer comparison" data-testid="retailer-comparison-table">
        <TableHead>
          <TableRow>
            <SortableHeader sort={sort} field="retailer" label="Retailer" onChange={onSortChange} />
            <SortableHeader sort={sort} field="price" label="Price" onChange={onSortChange} />
            <SortableHeader sort={sort} field="pricePerRound" label="CPR" onChange={onSortChange} />
            <SortableHeader sort={sort} field="roundCount" label="Rounds" onChange={onSortChange} />
            <TableCell>Stock</TableCell>
            <SortableHeader
              sort={sort}
              field="freshness"
              label="Freshness"
              onChange={onSortChange}
            />
            <TableCell>Last verified</TableCell>
            <TableCell>Buy</TableCell>
            <TableCell>Watch</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {listings.map((l) => (
            <RetailerRow
              key={l.id}
              listing={l}
              onOutboundClick={onOutboundClick}
              watchedItemId={watchedByListingId.get(l.id) ?? null}
              onWatchChanged={onWatchChanged}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SortableHeader({
  sort,
  field,
  label,
  onChange,
}: {
  sort: SortState;
  field: SortField;
  label: string;
  onChange: (field: SortField) => void;
}) {
  const active = sort.field === field;
  return (
    <TableCell sortDirection={active ? sort.dir : false}>
      <TableSortLabel
        active={active}
        direction={active ? sort.dir : 'asc'}
        onClick={() => onChange(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

function RetailerRow({
  listing,
  onOutboundClick,
  watchedItemId,
  onWatchChanged,
}: {
  listing: ProductListing;
  onOutboundClick: (listing: ProductListing) => void;
  watchedItemId: string | null;
  onWatchChanged: () => void;
}) {
  const retailer = listing.Retailer;
  const trust = retailer?.trustScore == null ? null : Number(retailer.trustScore);
  return (
    <TableRow hover>
      <TableCell>
        {retailer ? (
          <Stack>
            <MuiLink component={RouterLink} to={`/retailers/${retailer.slug || retailer.id}`}>
              {retailer.name}
            </MuiLink>
            {trust != null && Number.isFinite(trust) && (
              <Typography variant="caption" color="text.secondary">
                Trust {trust.toFixed(2)} / 1.00
              </Typography>
            )}
          </Stack>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell>{formatMoney(listing.price, listing.currency)}</TableCell>
      <TableCell>{formatCpr(listing.pricePerRound)}</TableCell>
      <TableCell>{formatRoundCount(effectiveRoundCount(listing))}</TableCell>
      <TableCell>{STOCK_LABEL[listing.stockStatus]}</TableCell>
      <TableCell>
        <Chip
          size="small"
          label={FRESHNESS_LABEL[listing.dataFreshness]}
          color={FRESHNESS_COLOR[listing.dataFreshness]}
        />
      </TableCell>
      <TableCell>{formatDate(listing.lastVerifiedAt)}</TableCell>
      <TableCell>
        <Tooltip title="You'll be leaving Round Recon to view this listing on the retailer's site.">
          <Button
            variant="contained"
            color="primary"
            size="small"
            endIcon={<LaunchIcon fontSize="small" />}
            href={`/api/out/${listing.id}`}
            target="_blank"
            rel="noopener external nofollow"
            onClick={() => onOutboundClick(listing)}
            aria-label={`Buy at ${retailer?.name ?? 'retailer'} (opens in new tab, leaving Round Recon)`}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Buy at {retailer?.name ?? 'retailer'}
          </Button>
        </Tooltip>
      </TableCell>
      <TableCell>
        <WatchButton
          listingId={listing.id}
          watchedItemId={watchedItemId}
          contextLabel={retailer?.name ? `this listing at ${retailer.name}` : 'this listing'}
          onChanged={onWatchChanged}
        />
      </TableCell>
    </TableRow>
  );
}

/**
 * Price History section — fetches the price-history endpoint for the given
 * product and renders an accessible SVG line chart with a data-table
 * fallback. Loading, empty, and error states are all handled.
 */
function PriceHistorySection({
  productId,
  productName,
}: {
  productId: string;
  productName: string;
}) {
  const historyQuery = useQuery({
    queryKey: ['product', productId, 'price-history'],
    queryFn: () => productsApi.getPriceHistory(productId),
    retry: false,
  });

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-labelledby="price-history-heading"
    >
      <Typography id="price-history-heading" variant="h2" sx={{ fontSize: '1.25rem', mb: 2 }}>
        Price history
      </Typography>

      {historyQuery.isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} aria-label="Loading price history" />
          <Typography variant="body2" color="text.secondary">
            Loading price history…
          </Typography>
        </Box>
      )}

      {historyQuery.isError && (
        <Alert severity="warning" role="status" data-testid="price-history-error">
          Price history is temporarily unavailable. The chart will appear once the service is
          restored.
        </Alert>
      )}

      {historyQuery.data && (
        <PriceHistoryChart series={historyQuery.data.series} productName={productName} />
      )}
    </Paper>
  );
}

function AttributeExplanations() {
  return (
    <Accordion variant="outlined" disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="attr-explanations"
        id="attr-explanations-header"
      >
        <Typography variant="subtitle1">What do these terms mean?</Typography>
      </AccordionSummary>
      <AccordionDetails id="attr-explanations">
        <Box component="dl" sx={{ m: 0 }}>
          {ATTRIBUTE_EXPLANATIONS.map((row) => (
            <Box key={row.term} sx={{ mb: 1 }}>
              <Typography component="dt" variant="body2" sx={{ fontWeight: 600 }}>
                {row.term}
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                {row.explanation}
              </Typography>
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

/** How many similar products to surface. */
const SIMILAR_LIMIT = 4;
/** Over-fetch so dedup-by-product + excluding the current product still fills the row. */
const SIMILAR_FETCH_LIMIT = 24;

/**
 * Similar products (#118). "Similar" is defined as the same caliber — the
 * strongest practical substitute axis for ammunition — surfaced in-stock and
 * cheapest-first, with same-bullet-type matches boosted to the front so a
 * defensive-JHP shopper sees other JHP before FMJ. Results are deduped by
 * product and exclude the product being viewed.
 *
 * The section renders nothing when there's no caliber to match on, or once a
 * resolved query yields no other products — an empty "Similar products" box
 * is worse than no box.
 */
function SimilarProducts({
  productId,
  caliber,
  bulletType,
}: {
  productId: string;
  caliber: string | null;
  bulletType: string | null;
}) {
  const enabled = Boolean(caliber);
  const query = useQuery<SearchResponse>({
    queryKey: ['product', productId, 'similar', caliber],
    queryFn: () =>
      searchListings({
        caliber: caliber as string,
        inStockOnly: true,
        sort: 'pricePerRound',
        sortDir: 'asc',
        limit: SIMILAR_FETCH_LIMIT,
      }),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Dedupe by product, drop the product being viewed, keep cheapest per product
  // (results arrive CPR-ascending). Then stably boost same-bullet-type matches.
  const seen = new Set<string>([productId]);
  const picks: SearchHit[] = [];
  for (const hit of query.data?.results ?? []) {
    if (!hit.productId || seen.has(hit.productId)) continue;
    seen.add(hit.productId);
    picks.push(hit);
  }
  const sameType = (h: SearchHit) =>
    bulletType && h.attributes?.bulletType === bulletType ? 0 : 1;
  picks.sort((a, b) => sameType(a) - sameType(b));
  const items = picks.slice(0, SIMILAR_LIMIT);

  // Render nothing until there's something to show: no caliber to match on, or
  // no other products (incl. while loading). A "Similar products" heading that
  // flashes in and then vanishes is worse than the section appearing when ready.
  if (!enabled || items.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-labelledby="similar-heading">
      <Typography id="similar-heading" variant="h2" sx={{ fontSize: '1.25rem', mb: 1 }}>
        Similar products
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Other in-stock {caliber} options, lowest price per round first.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        }}
      >
        {items.map((hit) => (
          <ListingCard key={hit.listingId} hit={hit} variant="compact" />
        ))}
      </Box>
    </Paper>
  );
}

function AlertsCta() {
  return (
    <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-labelledby="alerts-heading">
      <Typography id="alerts-heading" variant="h2" sx={{ fontSize: '1.25rem', mb: 1 }}>
        Alerts for this product
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Get notified when this product hits a target price, comes back in stock, or drops below a
        CPR threshold.
      </Typography>
      <Button component={RouterLink} to="/login" variant="contained">
        Sign in to create an alert
      </Button>
    </Paper>
  );
}

function ReportIssueDialog({
  open,
  onClose,
  productId,
}: {
  open: boolean;
  onClose: () => void;
  productId: string;
}) {
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function handleClose() {
    // Reset so the next open starts clean.
    setDescription('');
    setError(null);
    setDone(false);
    setSubmitting(false);
    onClose();
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      await productsApi.reportIssue(productId, description.trim());
      setDone(true);
    } catch {
      setError('Sorry — we couldn’t submit your report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="report-issue-title">
      <DialogTitle id="report-issue-title">Report an issue with this listing</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success" role="status">
            Thanks — your report was submitted and will be reviewed.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Tell us what looks wrong — e.g. price/stock mismatch, broken retailer link, or
              misclassified ammo. We use your reports to improve normalization and trust scores.
            </Typography>
            {error && (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              label="What's wrong?"
              multiline
              minRows={3}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {done ? (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={description.trim().length === 0 || submitting}
              aria-busy={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit report'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function getErrorState(error: unknown): { kind: 'notFound' | 'apiDown'; message: string } {
  if (error instanceof ApiError) {
    if (error.httpStatus === 404) {
      return { kind: 'notFound', message: 'We couldn’t find that product.' };
    }
    return { kind: 'apiDown', message: error.serverMessage || error.message };
  }
  return { kind: 'apiDown', message: (error as Error)?.message ?? 'Unknown error' };
}

/**
 * Product detail page (spec §15.5).
 *
 * Displays normalized product info, an aggregate confidence + worst-freshness
 * banner, a sortable retailer comparison table (default sort: CPR ascending),
 * an attribute-explanation accordion, and stubbed sections for similar
 * products, alert creation, and user feedback.
 */
export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [sort, setSort] = useState<SortState>({ field: 'pricePerRound', dir: 'asc' });
  const [reportOpen, setReportOpen] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const t = theme.palette.tactical;

  const query = useQuery({
    queryKey: ['product', id],
    queryFn: () => productsApi.getProduct(id ?? ''),
    enabled: Boolean(id),
    retry: false,
  });

  // GA4 `view_item` event (#256) when the product loads. Consent-gated inside
  // the helper; no-op without analytics consent / GA configured.
  const viewedProduct = query.data?.product;
  useEffect(() => {
    if (!viewedProduct) return;
    trackViewItem({
      id: viewedProduct.id,
      name: buildHeading(viewedProduct),
      brand: viewedProduct.brand ?? undefined,
      caliber: viewedProduct.caliber ?? undefined,
    });
  }, [viewedProduct]);

  // Watchlist state drives the per-row Watch toggle. Only fetched when
  // signed in (logged-out users get a "sign in to watch" route instead).
  const watchlistQuery = useQuery({
    queryKey: ['user', 'watchlist'],
    queryFn: () => userApi.listWatchlist(),
    enabled: isAuthenticated,
  });

  // Map ProductListing id → watchlist *item* id so each row knows
  // whether it's already watched and, if so, which item to remove.
  const watchedByListingId = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of watchlistQuery.data?.watchlist ?? []) {
      if (item.productListingId) map.set(item.productListingId, item.id);
    }
    return map;
  }, [watchlistQuery.data]);

  function invalidateWatchlist() {
    void queryClient.invalidateQueries({ queryKey: ['user', 'watchlist'] });
  }

  const sortedListings = useMemo(() => {
    const listings = query.data?.listings ?? [];
    return [...listings].sort((a, b) => compareListings(a, b, sort));
  }, [query.data, sort]);

  // True cheapest offer (by $/round) regardless of the table's current sort —
  // drives the sticky price summary.
  const cheapest = useMemo<ProductListing | null>(() => {
    const listings = query.data?.listings ?? [];
    if (listings.length === 0) return null;
    return [...listings].sort((a, b) => pricePerRoundNumber(a) - pricePerRoundNumber(b))[0] ?? null;
  }, [query.data]);

  // Price-history range for the "vs range" indicator. Shares the React Query
  // cache key with <PriceHistorySection>, so this is not an extra round-trip.
  const historyQuery = useQuery({
    queryKey: ['product', id, 'price-history'],
    queryFn: () => productsApi.getPriceHistory(id ?? ''),
    enabled: Boolean(id),
    retry: false,
  });
  const cprRange = useMemo(() => {
    const values: number[] = [];
    for (const series of historyQuery.data?.series ?? []) {
      for (const point of series.points) {
        const n = point.pricePerRound == null ? NaN : Number(point.pricePerRound);
        if (Number.isFinite(n) && n > 0) values.push(n);
      }
    }
    if (values.length < 2) return null;
    return { low: Math.min(...values), high: Math.max(...values) };
  }, [historyQuery.data]);

  function handleSortChange(field: SortField) {
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'asc' }
    );
  }

  if (!id) {
    return (
      <Alert severity="error" role="alert">
        Missing product id in the URL.
      </Alert>
    );
  }

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading product" />
      </Box>
    );
  }

  if (query.isError) {
    const { kind, message } = getErrorState(query.error);
    return (
      <Alert severity={kind === 'notFound' ? 'warning' : 'error'} role="alert">
        {kind === 'notFound'
          ? 'Product not found. It may have been removed or never existed. '
          : 'We couldn’t load this product right now. '}
        <Typography component="span" variant="body2" color="text.secondary">
          {message}
        </Typography>
      </Alert>
    );
  }

  if (!query.data) return null;

  const data: ProductDetailResponse = query.data;
  const heading = buildHeading(data.product);
  const confidence = aggregateConfidence(data.product, data.listings);
  const freshness = worstFreshness(data.listings);

  // Canonical URL prefers the slug per ADR 0004 §16.4.6. Falls back
  // to the UUID for legacy products that haven't been re-normalized
  // since the slug column was added.
  const canonical = buildCanonicalUrl(`/products/${data.product.slug || data.product.id}`);
  const productLd = productJsonLd({
    name: heading,
    description: buildProductDescription(data),
    sku: data.product.upc ?? data.product.manufacturerPartNumber ?? undefined,
    brand: data.product.brand ?? undefined,
    category: data.product.caliber ?? undefined,
    url: canonical,
    offers: data.listings.map((l) => ({
      price: typeof l.price === 'string' ? Number(l.price) : l.price,
      sellerName: l.Retailer?.name ?? 'Retailer',
      availability: l.stockStatus,
      url: l.sourceUrl,
      // Effective $/round (indexed, or derived from price ÷ rounds) so the
      // Offer can carry a machine-readable UnitPriceSpecification (#255).
      pricePerRound: effectiveCpr({
        pricePerRound:
          typeof l.pricePerRound === 'string' ? Number(l.pricePerRound) : l.pricePerRound,
        price: typeof l.price === 'string' ? Number(l.price) : l.price,
        roundCount: l.roundCount,
      }),
      // Machine-readable price validity derived from listing provenance +
      // freshness, so AI/search engines know how current the price is (#254).
      priceValidUntil: offerPriceValidUntil({
        lastVerifiedAt: l.lastVerifiedAt,
        lastScrapedAt: l.lastScrapedAt,
        dataFreshness: l.dataFreshness,
      }),
    })),
  });
  const breadcrumbsLd = breadcrumbList([
    { name: 'Round Recon', url: '/' },
    { name: 'Products', url: '/search' },
    { name: heading, url: canonical },
  ]);

  const bestCpr = cheapest ? pricePerRoundNumber(cheapest) : Number.POSITIVE_INFINITY;
  const specStrip: Array<{ label: string; value: string }> = [
    data.product.bulletType ? { label: 'Bullet', value: data.product.bulletType } : null,
    data.product.bulletWeightGrains != null
      ? { label: 'Weight', value: formatGrains(data.product.bulletWeightGrains) }
      : null,
    data.product.caseMaterial ? { label: 'Casing', value: data.product.caseMaterial } : null,
    data.product.primerType ? { label: 'Primer', value: data.product.primerType } : null,
  ].filter((x): x is { label: string; value: string } => x != null);

  return (
    <Stack spacing={4} component="article" aria-labelledby="product-heading">
      <Seo
        title={heading}
        description={buildProductDescription(data)}
        canonical={canonical}
        ogType="product"
        jsonLd={[productLd, breadcrumbsLd]}
      />

      {/* Breadcrumb */}
      <Box
        component="nav"
        aria-label="Breadcrumb"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily: t.monoFont,
          fontSize: 11,
          color: t.dim,
          flexWrap: 'wrap',
        }}
      >
        <Box
          component={RouterLink}
          to="/"
          sx={{ color: t.dim, textDecoration: 'none', '&:hover': { color: t.text } }}
        >
          HOME
        </Box>
        <Box component="span" sx={{ color: t.dimmer }}>
          /
        </Box>
        {data.product.caliber && (
          <>
            <Box
              component={RouterLink}
              to={`/search?caliber=${encodeURIComponent(data.product.caliber)}`}
              sx={{
                color: t.dim,
                textDecoration: 'none',
                textTransform: 'uppercase',
                '&:hover': { color: t.text },
              }}
            >
              {data.product.caliber}
            </Box>
            <Box component="span" sx={{ color: t.dimmer }}>
              /
            </Box>
          </>
        )}
        <Box component="span" sx={{ color: t.accent, textTransform: 'uppercase' }}>
          {heading}
        </Box>
      </Box>

      {/* Hero: gallery + summary (left) · sticky price (right) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 380px' },
          gap: { xs: 3, md: 5 },
          alignItems: 'start',
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            {cheapest && Number.isFinite(bestCpr) && (
              <Chip
                label="Cheapest right now"
                size="small"
                sx={{
                  bgcolor: t.accent,
                  color: t.accentInk,
                  fontFamily: t.monoFont,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                }}
              />
            )}
            {data.product.upc || data.product.manufacturerPartNumber ? (
              <Chip
                label={`SKU · ${data.product.upc ?? data.product.manufacturerPartNumber}`}
                size="small"
                variant="outlined"
                sx={{ fontFamily: t.monoFont, fontSize: 10 }}
              />
            ) : null}
          </Stack>

          <Typography
            id="product-heading"
            variant="h1"
            sx={{ fontSize: { xs: '1.9rem', md: '2.6rem' } }}
          >
            {heading}
          </Typography>
          <Typography variant="body1" sx={{ color: t.dim, mt: 0.75 }}>
            {[
              data.product.caliber,
              formatGrains(data.product.bulletWeightGrains),
              data.product.bulletType,
            ]
              .filter((p) => p && String(p).trim() && p !== '—')
              .join(' · ')}
          </Typography>

          {/* Cartridge hero */}
          <Box
            sx={{
              mt: 3,
              height: { xs: 200, md: 300 },
              position: 'relative',
              background: `repeating-linear-gradient(135deg, ${t.panelHi} 0, ${t.panelHi} 8px, ${t.panel} 8px, ${t.panel} 16px)`,
              border: `1px solid ${t.line}`,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Cartridge
              caliber={data.product.caliber}
              ammoType={data.product.ammoType}
              height={140}
              accent
              decorative
            />
          </Box>

          {/* Spec strip */}
          {specStrip.length > 0 && (
            <Box
              sx={{
                mt: 3,
                py: 2.5,
                borderTop: `1px solid ${t.line}`,
                borderBottom: `1px solid ${t.line}`,
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: `repeat(${specStrip.length}, 1fr)`,
                },
                gap: 3,
              }}
            >
              {specStrip.map((s) => (
                <Box key={s.label}>
                  <Typography
                    sx={{
                      fontFamily: t.monoFont,
                      fontSize: 10,
                      color: t.dim,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {s.label}
                  </Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', mt: 0.5 }}>
                    {s.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <ConfidenceFreshnessBanner confidence={confidence} freshness={freshness} />
          </Box>
        </Box>

        {/* Sticky price aside */}
        <Box
          component="aside"
          aria-label="Best price summary"
          sx={{
            position: { md: 'sticky' },
            top: { md: 88 },
            bgcolor: t.panel,
            border: `1px solid ${t.line}`,
            borderRadius: 3,
            p: 3,
          }}
        >
          {cheapest ? (
            <>
              <Eyebrow component="div" color={t.accent} sx={{ mb: 1 }}>
                ● Best price across {data.listings.length}{' '}
                {data.listings.length === 1 ? 'retailer' : 'retailers'}
              </Eyebrow>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 1 }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '3rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    letterSpacing: '-0.03em',
                  }}
                >
                  {formatCpr(cheapest.pricePerRound)}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: t.dim, mt: 1 }}>
                {formatMoney(cheapest.price, cheapest.currency)}
                {cheapest.roundCount ? ` per box of ${cheapest.roundCount}` : ''} at{' '}
                {cheapest.Retailer?.name ?? 'retailer'}
              </Typography>

              <Stack spacing={1} sx={{ mt: 2.5 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  endIcon={<LaunchIcon fontSize="small" />}
                  href={`/api/out/${cheapest.id}`}
                  target="_blank"
                  rel="noopener external nofollow"
                  onClick={() =>
                    trackOutboundClick({
                      listingId: cheapest.id,
                      retailerId: cheapest.retailerId,
                      sourceUrl: cheapest.sourceUrl,
                      retailer: cheapest.Retailer?.name,
                      productName: heading,
                      caliber: data.product.caliber,
                    })
                  }
                  aria-label={`Buy at ${cheapest.Retailer?.name ?? 'retailer'} (opens in new tab, leaving Round Recon)`}
                >
                  Buy at {cheapest.Retailer?.name ?? 'retailer'}
                </Button>
                <WatchButton
                  listingId={cheapest.id}
                  watchedItemId={watchedByListingId.get(cheapest.id) ?? null}
                  contextLabel={
                    cheapest.Retailer?.name
                      ? `the cheapest listing (at ${cheapest.Retailer.name})`
                      : 'the cheapest listing'
                  }
                  onChanged={invalidateWatchlist}
                />
              </Stack>

              <Box
                sx={{
                  mt: 3,
                  pt: 2.5,
                  borderTop: `1px solid ${t.line}`,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: t.monoFont,
                      fontSize: 10,
                      color: t.dim,
                      letterSpacing: '0.1em',
                    }}
                  >
                    STOCK
                  </Typography>
                  <Box sx={{ mt: 0.75 }}>
                    <StockPill status={cheapest.stockStatus} size="sm" />
                  </Box>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: t.monoFont,
                      fontSize: 10,
                      color: t.dim,
                      letterSpacing: '0.1em',
                    }}
                  >
                    FRESHNESS
                  </Typography>
                  <Typography sx={{ mt: 0.75, fontSize: 13 }}>
                    {FRESHNESS_LABEL[cheapest.dataFreshness]}
                  </Typography>
                </Box>
              </Box>

              {cprRange && Number.isFinite(bestCpr) && (
                <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${t.line}` }}>
                  <Typography
                    sx={{
                      fontFamily: t.monoFont,
                      fontSize: 10,
                      color: t.dim,
                      letterSpacing: '0.1em',
                      mb: 1,
                    }}
                  >
                    VS OBSERVED RANGE
                  </Typography>
                  <PricePosition low={cprRange.low} high={cprRange.high} current={bestCpr} />
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ color: t.dim }}>
              No live offers right now — check the comparison below.
            </Typography>
          )}
        </Box>
      </Box>

      {/* All offers */}
      <Box component="section" aria-labelledby="retailers-heading">
        <Typography id="retailers-heading" variant="h2" sx={{ fontSize: '1.25rem', mb: 1.5 }}>
          Retailer comparison ({data.listings.length})
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Purchases are completed on third-party retailer websites under their terms. Round Recon
          does not process payments or verify eligibility. See our{' '}
          <MuiLink component={RouterLink} to="/responsibility">
            User responsibility notice
          </MuiLink>
          .
        </Typography>
        <RetailerComparisonTable
          listings={sortedListings}
          sort={sort}
          onSortChange={handleSortChange}
          watchedByListingId={watchedByListingId}
          onWatchChanged={invalidateWatchlist}
          onOutboundClick={(l) =>
            trackOutboundClick({
              listingId: l.id,
              retailerId: l.retailerId,
              sourceUrl: l.sourceUrl,
              retailer: l.Retailer?.name,
              productName: heading,
              caliber: data.product.caliber,
            })
          }
        />
      </Box>

      <PriceHistorySection productId={data.product.id} productName={heading} />

      <ProductInfoCard product={data.product} />

      <AttributeExplanations />

      <Divider />

      <SimilarProducts
        productId={data.product.id}
        caliber={data.product.caliber}
        bulletType={data.product.bulletType}
      />

      <AlertsCta />

      <Box>
        <MuiLink component="button" type="button" onClick={() => setReportOpen(true)}>
          Report an issue with this listing
        </MuiLink>
      </Box>

      <ReportIssueDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        productId={data.product.id}
      />
    </Stack>
  );
}

export default ProductDetailPage;
